import React from 'react'
import cx from 'classnames'
import { Segment, CurrencyField, FundDistribution } from '../../../..'
import TaskList from '../../../../atoms/Svgs/TaskList'
import styles from './PaymentAllocation.module.scss'
import { subscribe } from 'react-contextual'
import { InvoicesProvider } from '../../../../../providers'

interface InvoiceType {
  name: string | null
  value: string | null
}

interface BeneficiaryValue {
  name?: string
  partyId?: string
  partyTag?: string
  reference?: string
  amount?: number | string
  vat?: boolean
}

interface Beneficiary {
  beneficiary?: {
    type?: string
    value?: BeneficiaryValue
  }
}

interface PartyTag {
  value: string
  label: string
}

interface PaymentAllocationProps {
  errors?: any
  invoiceId?: string
  invoiceType?: InvoiceType
  invoiceStatus?: string
  grossAmount?: number
  balance?: number
  availableFunds: number | string
  onNewBeneficiaryAdded?: (beneficiary: any) => any
  onNewBeneficiaryAmountChange?: (...args: any[]) => any
  onBeneficiaryReferenceChange?: (...args: any[]) => any
  onDepositBeneficiaryTransferChange: (...args: any[]) => any
  onBeneficiariesOrderChange?: (...args: any[]) => any
  onBeneficiaryRemoved?: (...args: any[]) => any
  onVatChange?: (...args: any[]) => any
  beneficiaries?: Beneficiary[]
  /** List of party tags for selection */
  partyTags?: PartyTag[]
  propertyOwnerId?: string
  propertyOwnerName?: string
  propertyOwnerBankDetails?: object
  Footer?: React.ReactNode | any
  inputTooltipText?: string
  portfolioId?: string
  /** Injected by the InvoicesProvider subscription */
  easyPayData?: any
  fetchEasyPayData?: (...args: any[]) => any
  [key: string]: any
}

type BeforeContentProps = {
  availableFunds: number | string
}

const BeforeContent = ({ availableFunds }: BeforeContentProps): React.ReactElement => (
  <Segment horizontal>
    <Segment.Label icon={<TaskList />} text="Available Funds" />
    <div className={styles.amount}>
      <CurrencyField disabled name="availableFunds" value={availableFunds} />
    </div>
  </Segment>
)

const PaymentAllocation = (props: PaymentAllocationProps): React.ReactElement => {
  const {
    errors,
    invoiceStatus,
    availableFunds,
    balance,
    onNewBeneficiaryAdded = (beneficiary: any) => beneficiary,
    onNewBeneficiaryAmountChange,
    onBeneficiaryReferenceChange,
    onDepositBeneficiaryTransferChange,
    onBeneficiariesOrderChange,
    onBeneficiaryRemoved,
    onVatChange,
    beneficiaries = [],
    invoiceId,
    invoiceType = { name: null, value: null },
    partyTags,
    easyPayData,
    fetchEasyPayData,
    propertyOwnerId,
    propertyOwnerName,
    propertyOwnerBankDetails,
    Footer,
    inputTooltipText,
    portfolioId,
    ...rest
  } = props

  const classes = cx('root', {
    'status-ready': invoiceStatus === 'Ready',
  })

  return (
    <div className={classes}>
      <FundDistribution
        invoiceId={invoiceId}
        invoiceType={invoiceType}
        errors={errors}
        state={invoiceStatus === 'Short' || invoiceStatus === 'NotSet' ? 'warning' : 'default'}
        beforeContent={<BeforeContent availableFunds={availableFunds} />}
        headerText="Payment Allocation"
        invoiceStatus={invoiceStatus}
        balance={balance}
        includeVatCalculatorOnPaymentRules={false}
        onNewBeneficiaryAdded={onNewBeneficiaryAdded}
        onNewBeneficiaryAmountChange={onNewBeneficiaryAmountChange}
        onBeneficiaryReferenceChange={onBeneficiaryReferenceChange}
        onDepositBeneficiaryTransferChange={onDepositBeneficiaryTransferChange}
        onBeneficiariesOrderChange={onBeneficiariesOrderChange}
        beneficiaries={beneficiaries}
        onVatChange={onVatChange}
        partySearchTags=""
        partyTags={partyTags}
        disableDepositEdit={false}
        onBeneficiaryRemoved={onBeneficiaryRemoved}
        easyPayData={easyPayData}
        fetchEasyPayData={fetchEasyPayData}
        propertyOwnerId={propertyOwnerId}
        propertyOwnerName={propertyOwnerName}
        propertyOwnerBankDetails={propertyOwnerBankDetails}
        Footer={Footer}
        inputTooltipText={inputTooltipText}
        portfolioId={portfolioId}
        {...rest}
      />
    </div>
  )
}

export default subscribe(
  [InvoicesProvider],
  ({ fetchEasyPayData, easyPayData, propertyOwnerId, propertyOwnerName, propertyOwnerBankDetails }: any) => ({
    fetchEasyPayData,
    easyPayData,
    propertyOwnerId,
    propertyOwnerName,
    propertyOwnerBankDetails,
  }),
)(PaymentAllocation)
